import { pick } from 'lodash';
import { DateTime } from 'luxon';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { calculateLightUser } from '@work4all/components/lib/components/entity-preview/user-preview/components/LicenseExchangeTab';

import { useDataMutation } from '@work4all/data';

import { FullUser } from '@work4all/models/lib/Classes/FullUser.entity';
import { InputFullUserRelation } from '@work4all/models/lib/Classes/InputFullUserRelation.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { LicenseAssignment } from '@work4all/models/lib/Enums/LicenseAssignment.enum';
import { UserClass } from '@work4all/models/lib/Enums/UserClass.enum';

import { MaskTab, MaskTabPanel, MaskTabs } from '../../../mask-tabs';
import { MaskContent } from '../../components/MaskContent/MaskContent';
import { useMaskConfig } from '../../hooks/mask-context';
import { useAfterSave } from '../../hooks/use-after-save';
import { useOverrideProps } from '../../hooks/use-override-props';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';

import { ExchangeTabPanel } from './components/exchange/ExchangeTabPanel';
import { GeneralTabPanel } from './components/general/GeneralTabPanel';
import { LicenseTabPanel } from './components/license/LicenseTabPanel';
import { PasswordChangeButton } from './components/password-change/PasswordChangeButton';
import { PersonalTabPanel } from './components/personal/PersonalTabPanel';
import { SecurityTabPanel } from './components/security/SecurityTabPanel';
import { makeEmptyNowDate } from './components/work-time/getTimeValue';
import { WorkTimeTabPanel } from './components/work-time/WorkTimeTabPanel';
import { useUserFormUpdate } from './hooks/use-user-form-update';
import { useUserRequest } from './hooks/use-user-request';
import { makeOnMutationError } from './MakeOnMutationError';
import { UserFormValue } from './types';

export const UserOverlayController: React.FC<MaskControllerProps> = (props) => {
  const { lastProps, overrideProps } = useOverrideProps(props);
  const mask = useMaskConfig(lastProps);
  const request = useUserRequest({ id: mask.id });

  const normalizeData = useCallback(
    (
      input: UserFormValue,
      newEntityData: UserFormValue,
      isCreateMode: boolean
    ) => {
      const output = { ...input, ...(isCreateMode ? newEntityData : {}) };
      if (output.birthDate === '0001-01-01T00:00:00') output.birthDate = null;
      if (output.enterDate === '0001-01-01T00:00:00') output.enterDate = null;
      if (output.exitDate === '0001-01-01T00:00:00') output.exitDate = null;

      const getDay = (targets, weekday) => {
        const target = (targets || []).find(
          (entry) => entry.weekday === weekday
        );
        return target ? target.dailytarget : 0; // Default to 0 if not found
      };
      const userWorkTimeDaylieTargets = output.userWorkTimeDaylieTargets;
      output.dailyTargetWorkHoursMonday = getDay(userWorkTimeDaylieTargets, 0);
      output.dailyTargetWorkHoursTuesday = getDay(userWorkTimeDaylieTargets, 1);
      output.dailyTargetWorkHoursWednesday = getDay(
        userWorkTimeDaylieTargets,
        2
      );
      output.dailyTargetWorkHoursThursday = getDay(
        userWorkTimeDaylieTargets,
        3
      );
      output.dailyTargetWorkHoursFriday = getDay(userWorkTimeDaylieTargets, 4);
      output.dailyTargetWorkHoursSaturday = getDay(
        userWorkTimeDaylieTargets,
        5
      );
      output.dailyTargetWorkHoursSunday = getDay(userWorkTimeDaylieTargets, 6);
      output.earliestCommingTime = DateTime.now()
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .plus({ minutes: output.comesGoesMinimumStartTime || 0 })
        .toISO();

      output.birthDate = DateTime.fromISO(output.birthDate).toISO();

      output.enterDate = DateTime.fromISO(output.enterDate).toISO();

      output.exitDate = DateTime.fromISO(output.exitDate).toISO();

      if (output.roundingForHour) {
        output.roundingForHour = DateTime.fromISO(
          output.roundingForHour
        ).toISO();
      } else {
        output.roundingForHour = makeEmptyNowDate().toISO();
      }

      // save isLightUser as it is a radio button on the ui so we need temporary state
      output.isLightUser = calculateLightUser(output);
      output.wasFullUser = output.userKind === UserClass.BENUTZER;
      output.originalAssignedLicense = output.assignedLicenses;
      return output;
    },
    []
  );

  const { t } = useTranslation();

  const customRules = useCallback(
    (data: UserFormValue) => {
      if (data.userKind === UserClass.RESSOURCE) return true;

      let requiredFields: string[] = [];
      if (data.isLightUser) {
        requiredFields = ['eMail', 'shortName', 'firstName', 'lastName'];
      } else if (data.userKind === UserClass.MITARBEITER) {
        requiredFields = ['shortName', 'firstName', 'lastName'];
      } else {
        requiredFields = [
          'name',
          'eMail',
          'shortName',
          'firstName',
          'lastName',
        ];
      }

      const errors = [];
      for (const field of requiredFields) {
        if (!data[field]) {
          errors.push({
            [field]: {
              message: t('ERROR.FIELD_REQUIRED'),
              type: 'customValidation',
            },
          });
        }
      }

      if (data.isLightUser) {
        const hasLightLicense = calculateLightUser(data);
        if (!hasLightLicense) {
          errors.push({
            assignedLicenses: {
              message: t('ERROR.LIGHT_USER_MUST_HAVE_LIGHT_LICENSE'),
            },
          });
        }
      }

      if (errors.length) {
        return errors.reduce((prev, cur) => {
          return { ...prev, ...cur };
        }, {});
      }

      return true;
    },
    [t]
  );

  const newEntityData = useMemo((): FullUser => {
    return {
      userKind: props.params?.userKind as UserClass,
      number: 0,
      vacationValue: 0,
      timing: 0,
      timestampIncludedMinutesPerMonth: 0,
      timestampIncludedMinutesPerDay: 0,
      comesGoesMinimalPause: 0,
      assignedLicenses: Object.values(LicenseAssignment).map((license) => ({
        assigned: false,
        license,
      })),
    };
  }, [props.params?.userKind]);

  const overlay = useMaskOverlay<UserFormValue>({
    ...lastProps,
    request,
    newEntityData,
    mask,
    getSubTitle: (x) => x.displayName,
    normalizeData,
    customRules,
  });

  useUserFormUpdate(overlay.form);

  const isCreatingUserFromSecurity = !overlay.data?.id;

  const onAfterSave = useAfterSave(
    lastProps.amplitudeEntryPoint || Entities.fullUser,
    isCreatingUserFromSecurity
  );

  const [mutate] = useDataMutation<
    FullUser,
    EMode.upsert,
    InputFullUserRelation
  >({
    entity: mask.entity,
    mutationType: EMode.upsert,
    responseData: request.data,
    onCompleted: (data) => {
      if (isCreatingUserFromSecurity) {
        overrideProps({ id: data.id.toString() });
        overlay.initialFormValue.refetch();
        onAfterSave();
      } else {
        props.onAfterSave(data);
      }
    },
    onError: makeOnMutationError<UserFormValue>(
      overlay.form,
      Entities.fullUser
    ),
  });

  const onSubmit = async (formData: UserFormValue) => {
    const toModify = pickUpdateFields(
      formData,
      overlay.form.formState.dirtyFields
    );
    const relations = mapUserRelations(toModify);

    if (toModify.earliestCommingTime) {
      toModify.comesGoesMinimumStartTime =
        DateTime.fromISO(toModify.earliestCommingTime).hour * 60 +
        DateTime.fromISO(toModify.earliestCommingTime).minute;
      delete toModify.earliestCommingTime;
    }

    if (
      formData.userKind === UserClass.RESSOURCE ||
      formData.userKind === UserClass.MITARBEITER
    ) {
      toModify.userKind = formData.userKind;
    }

    if (formData.userKind === UserClass.MITARBEITER) {
      toModify.displayName = formData.displayName
        ? formData.displayName
        : formData.shortName;
    }

    if (formData.assignedLicenses) {
      relations.setUserAssignedLicense = formData.assignedLicenses
        .filter((license) => {
          const originalLicense = formData.originalAssignedLicense?.find(
            (l) => l.license === license.license
          );
          return (
            !originalLicense || originalLicense.assigned !== license.assigned
          );
        })
        .map((license) => pick(license, ['license', 'assigned']));
    }
    if (formData.syncOptionsEdit) {
      relations.syncOptions = {
        addModify: [formData.syncOptionsEdit],
      };
    }
    mutate(toModify, { relations });
  };

  const userKind =
    (props.params?.userKind as UserClass) ?? overlay.data.userKind;
  const loading = props.params?.userKind ? false : overlay.loading;

  return (
    <OverlayController<UserFormValue>
      {...overlay}
      title={userKind ? t(`ENUMS.${userKind.toUpperCase()}`) : overlay.title}
      onSubmit={onSubmit}
      actions={<PasswordChangeButton />}
      tabs={<Tabs userClass={userKind} loading={loading} />}
      disableDelete
    >
      {loading ? <></> : <Content userClass={userKind} />}
    </OverlayController>
  );
};

interface TabsContentProps {
  userClass?: UserClass;
  loading?: boolean;
}

const Tabs = memo(function (props: TabsContentProps) {
  const { t } = useTranslation();
  const { userClass, loading } = props;
  const hidden = userClass === UserClass.RESSOURCE;
  const hiddenStyle =
    loading || hidden
      ? {
          display: 'none',
        }
      : undefined;

  return (
    <MaskTabs>
      <MaskTab value="general" label={t('MASK.GENERAL')} />
      <MaskTab
        style={hiddenStyle}
        value="misc"
        label={t('USER.PERSONAL_DATA_TAB')}
      />
      <MaskTab
        style={hiddenStyle}
        value="prices"
        label={t('USER.WORK_TIME_TAB')}
      />
      <MaskTab style={hiddenStyle} value="license" label={t('USER.LICENSE')} />
      {userClass !== UserClass.RESSOURCE && (
        <MaskTab value="exchange" label={t('USER.EXCHANGE')} />
      )}
      <MaskTab
        style={hiddenStyle}
        value="security"
        label={t('USER.SECURITY')}
      />
    </MaskTabs>
  );
});

const Content = memo(function (props: TabsContentProps) {
  const { userClass } = props;

  return (
    <MaskContent>
      <MaskTabPanel value="general">
        <GeneralTabPanel />
      </MaskTabPanel>
      {userClass !== UserClass.RESSOURCE && (
        <>
          <MaskTabPanel value="misc">
            <PersonalTabPanel />
          </MaskTabPanel>

          <MaskTabPanel value="prices">
            <WorkTimeTabPanel />
          </MaskTabPanel>
          <MaskTabPanel value="license">
            <LicenseTabPanel />
          </MaskTabPanel>
          <MaskTabPanel value="exchange">
            <ExchangeTabPanel />
          </MaskTabPanel>
          <MaskTabPanel value="security">
            <SecurityTabPanel />
          </MaskTabPanel>
        </>
      )}
    </MaskContent>
  );
});

const mapUserRelations = (input: UserFormValue): InputFullUserRelation => {
  const userImageFromTempFile = input.userImageFromTempFile;

  const dailyTimeTargets = {
    monday: input.dailyTargetWorkHoursMonday,
    tuesday: input.dailyTargetWorkHoursTuesday,
    wednesday: input.dailyTargetWorkHoursWednesday,
    thursday: input.dailyTargetWorkHoursThursday,
    friday: input.dailyTargetWorkHoursFriday,
    saturday: input.dailyTargetWorkHoursSaturday,
    sunday: input.dailyTargetWorkHoursSunday,
  };
  const hasDailyTimeTargets = Object.values(dailyTimeTargets).some(
    (value) => value !== undefined
  );

  return {
    ...(hasDailyTimeTargets && { dailyTimeTargets }),
    userImageFromTempFile,
  };
};
